/**
 * A casca: menu no topo, 48 px, e o miolo ocupando o resto da tela.
 *
 * Menu no topo e não na lateral porque o objetivo é espaço para conteúdo — foi
 * a decisão que sobreviveu às duas propostas. Uma faixa de comando por tela:
 * este menu, a faixa da própria tela, e o trabalho. Nada de quatro barras
 * empilhadas antes da primeira linha de dado.
 */

import { Link, NavLink, Outlet } from "react-router-dom"
import * as Menu from "@radix-ui/react-dropdown-menu"
import { usarSessao } from "@/sessao/Sessao"
import { usarTema } from "./Tema"
import { usarEscala } from "./Escala"
import { menuMais, menuPrincipal, type GrupoDeMenu, type ItemDeMenu } from "./navegacao"
import { alturaMaximaFlutuante, escalaFlutuante } from "@/kit/flutuante"
import { travaLigada } from "@/api/cliente"
import { BotaoDeFeedback } from "./BotaoDeFeedback"
import { RodapeDeVersao } from "./RodapeDeVersao"

const ALTURA = 48

export function Casca() {
  const { nome, pode, sair } = usarSessao()
  const { escuroAgora } = usarTema()

  const visiveis = menuPrincipal.filter((i) => !i.chave || pode(i.chave))
  const gruposVisiveis = menuMais
    .map((g) => ({ ...g, itens: g.itens.filter((i) => !i.chave || pode(i.chave)) }))
    .filter((g) => g.itens.length > 0)

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <header
        style={{
          height: ALTURA, flex: `0 0 ${ALTURA}px`,
          display: "flex", alignItems: "center", gap: 2,
          padding: "0 14px 0 16px", background: "var(--nav)",
          borderBottom: escuroAgora ? "1px solid rgba(51, 133, 214, 0.25)" : "none",
        }}
      >
        <Marca />

        <nav style={{ display: "flex", alignItems: "center" }}>
          {visiveis.map((item) => <ItemTopo key={item.para} item={item} />)}
          {gruposVisiveis.length > 0 && <MenuDoTopo rotulo="Mais" grupos={gruposVisiveis} />}
        </nav>

        {/* O que era o "Buscar" (um desenho de campo que não fazia nada) virou espaço:
            empurra os botões para a direita. */}
        <span style={{ flex: 1 }} />

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {travaLigada && <SeloDaTrava />}
          <BotoesDeEscala />
          <BotaoDeTema />
          <Avatar nome={nome} aoSair={sair} />
        </div>
      </header>

      {/*
        A escala vive aqui e não no <html>: cresce o conteúdo e deixa a barra de
        cima do tamanho que está. `zoom` porque as medidas do sistema são em px
        no próprio componente — recalcular cada uma por variável deixaria de
        fora qualquer lugar esquecido, e escala pela metade parece defeito.
        Diferente do Ctrl+ do navegador, que cresce a barra junto; os dois
        convivem, um por cima do outro.
      */}
      <main
        style={{
          flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
          zoom: "var(--escala, 1)",
        }}
      >
        <Outlet />
      </main>

      {/* Fora do <main> pelo mesmo motivo da barra de cima: não cresce com a escala. */}
      <RodapeDeVersao />

      {/* Fora do <main> de propósito: flutua por cima de qualquer tela e não
          cresce com a escala do conteúdo — o botão fica sempre do mesmo tamanho. */}
      <BotaoDeFeedback />
    </div>
  )
}

function Marca() {
  // A marca de verdade, do acervo: oc-logo-branco.png. É branca e laranja, feita
  // para fundo escuro — por isso vive na barra roxa e some sobre branco.
  // 368x150 no original; 26px de altura mantém a proporção sem esmagar o arco.
  return (
    <a
      href="/projetos"
      style={{ display: "flex", alignItems: "center", marginRight: 18, flex: "0 0 auto" }}
      aria-label="Oficina Conectada — Sistema"
    >
      <img
        src="/oc-logo.png"
        alt="Oficina Conectada"
        style={{ height: 26, width: "auto", display: "block" }}
      />
      <span
        className="marca-sufixo"
        style={{
          marginLeft: 9, paddingLeft: 9, borderLeft: "1px solid rgba(255,255,255,.22)",
          fontSize: 9.5, fontWeight: 600, letterSpacing: ".16em",
          color: "var(--nav-txt2)", textTransform: "uppercase",
        }}
      >
        Sistema
      </span>
    </a>
  )
}

const estiloItem = (ativo: boolean): React.CSSProperties => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  height: ALTURA,
  padding: "0 13px",
  fontSize: 13,
  fontWeight: 500,
  color: ativo ? "#FFFFFF" : "rgba(255,255,255,.68)",
})

function ItemTopo({ item }: { item: ItemDeMenu }) {
  if (item.emBreve) {
    return (
      <span
        title="Ainda não nesta volta — ver o calendário no plano de execução"
        style={{ ...estiloItem(false), color: "rgba(255,255,255,.34)", cursor: "default" }}
      >
        {item.rotulo}
      </span>
    )
  }
  return (
    <NavLink to={item.para} end={item.para === "/"} style={({ isActive }) => estiloItem(isActive)}>
      {({ isActive }) => (
        <>
          {item.rotulo}
          {isActive && (
            <span style={{ position: "absolute", left: 13, right: 13, bottom: 0, height: 2, borderRadius: 2, background: "var(--acao)" }} />
          )}
        </>
      )}
    </NavLink>
  )
}

/**
 * Um menu da barra de cima (o "Mais").
 *
 * **Cresce com a letra.** A barra fica do tamanho que está, mas o menu que ela
 * abre segue o A−/A+ (ver `kit/flutuante.ts`): é texto para ler, e quem aumentou
 * a letra precisa dele grande.
 */
export function MenuDoTopo({ rotulo, grupos, ativo = false }: {
  rotulo: string
  grupos: GrupoDeMenu[]
  ativo?: boolean
}) {
  return (
    <Menu.Root>
      <Menu.Trigger style={{ ...estiloItem(ativo), gap: 6 }}>
        {rotulo}
        <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
             style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
          <path d="m6 9.5 6 6 6-6" />
        </svg>
        {ativo && (
          <span style={{ position: "absolute", left: 13, right: 13, bottom: 0, height: 2, borderRadius: 2, background: "var(--acao)" }} />
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          sideOffset={0} align="start"
          style={{
            minWidth: 230, maxWidth: 360, padding: 6, borderRadius: 10,
            background: "var(--surface)", border: "1px solid var(--line2)",
            boxShadow: "var(--sombra)", color: "var(--txt)", zIndex: 50,
            // O Radix mede o espaço que sobra até a borda da janela; obedecer a ele
            // e rolar o resto é o que faz o menu caber em telas pequenas — e o teto
            // de 400 impede que fique grande demais nas maiores.
            maxHeight: alturaMaximaFlutuante(400),
            overflowY: "auto", overscrollBehavior: "contain",
            ...escalaFlutuante,
          }}
        >
          {grupos.map((g, i) => (
            <div key={g.rotulo}>
              {i > 0 && <Menu.Separator style={{ height: 1, background: "var(--line)", margin: "5px 0" }} />}
              <Menu.Label asChild>
                <div
                  style={{
                    marginBottom: 4, padding: "0 8px", fontSize: 9.5, fontWeight: 600,
                    letterSpacing: ".08em", textTransform: "uppercase", color: "var(--acao)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}
                  title={g.rotulo}
                >
                  {g.rotulo}
                </div>
              </Menu.Label>
              {g.aviso && (
                <p style={{ margin: 0, padding: "4px 9px 6px", fontSize: 11.5, lineHeight: 1.45, color: "var(--txt3)" }}>
                  {g.aviso}
                </p>
              )}
              {g.itens.map((item) => {
                const aparencia: React.CSSProperties = {
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, padding: "7px 9px", borderRadius: 6, fontSize: 12.5,
                  color: item.emBreve ? "var(--txt3)" : "var(--txt)",
                  cursor: item.emBreve ? "default" : "pointer", outline: "none",
                  textDecoration: "none",
                }

                // O item precisa ser um `<a>` de verdade, e nao so um texto dentro do
                // `Menu.Item`: sem isso o menu abria e nenhuma entrada levava a lugar
                // nenhum. Como link, tambem valem o clique do meio, o Ctrl+clique e o
                // "abrir em nova aba" — que e como se trabalha com duas telas abertas.
                if (item.emBreve) {
                  return (
                    <Menu.Item key={item.para} disabled style={aparencia}>
                      {item.rotulo}
                      <span style={{ fontSize: 10, color: "var(--txt3)" }}>em breve</span>
                    </Menu.Item>
                  )
                }

                return (
                  <Menu.Item key={item.para} asChild>
                    <Link to={item.para} style={aparencia}>{item.rotulo}</Link>
                  </Menu.Item>
                )
              })}
            </div>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}

/**
 * Menos / mais. Fica à vista na barra, e não dentro do menu do usuário: quem
 * tem dificuldade de enxergar é exatamente quem não vai caçar a opção.
 */
function BotoesDeEscala() {
  const { aumentar, diminuir, podeAumentar, podeDiminuir, porcento } = usarEscala()

  // Branco literal, como o botão de tema ao lado: a barra é escura nos dois
  // temas. (A primeira versão pedia `var(--nav-ink)`, que não existe — token
  // inválido não pinta nada, o botão herdava a cor do texto do corpo e ficava
  // escuro sobre roxo escuro.)
  const botao = (ligado: boolean): React.CSSProperties => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 30, height: 30, borderRadius: 7,
    color: ligado ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.38)",
    cursor: ligado ? "pointer" : "default",
  })

  return (
    <div
      role="group"
      aria-label={`Tamanho do conteúdo: ${porcento}%`}
      style={{ display: "flex", alignItems: "center", gap: 1 }}
    >
      <button
        onClick={diminuir}
        disabled={!podeDiminuir}
        title={`Diminuir o conteúdo (agora ${porcento}%)`}
        aria-label="Diminuir o tamanho do conteúdo"
        style={botao(podeDiminuir)}
      >
        <Letras pequena />
      </button>
      <button
        onClick={aumentar}
        disabled={!podeAumentar}
        title={`Aumentar o conteúdo (agora ${porcento}%)`}
        aria-label="Aumentar o tamanho do conteúdo"
        style={botao(podeAumentar)}
      >
        <Letras />
      </button>
      {/* O leitor de tela precisa saber que mudou; a tela já mostra. */}
      <span className="so-leitor" aria-live="polite">Conteúdo em {porcento} por cento</span>
    </div>
  )
}

/** Um "A" com a seta do sentido — o desenho que todo mundo já reconhece. */
function Letras({ pequena }: { pequena?: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <text x="0.5" y="18" style={{ fill: "currentColor", font: `700 ${pequena ? 13 : 18}px var(--ff)` }}>A</text>
      <path
        d={pequena ? "M19 7.5v9m0 0-2.6-2.8M19 16.5l2.6-2.8" : "M19 16.5v-9m0 0-2.6 2.8M19 7.5l2.6 2.8"}
        style={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}
      />
    </svg>
  )
}

function BotaoDeTema() {
  const { alternar, escuroAgora } = usarTema()
  return (
    <button
      type="button"
      onClick={alternar}
      aria-label={escuroAgora ? "Mudar para o tema claro" : "Mudar para o tema escuro"}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 30, height: 30, borderRadius: 7, color: "rgba(255,255,255,.75)",
      }}
    >
      {escuroAgora ? (
        <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true"
             style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" }}>
          <path d="M20.5 14.6A8.6 8.6 0 0 1 9.4 3.5a8.6 8.6 0 1 0 11.1 11.1Z" />
        </svg>
      ) : (
        <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true"
             style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" }}>
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.2 5.2l1.6 1.6M17.2 17.2l1.6 1.6M18.8 5.2l-1.6 1.6M6.8 17.2l-1.6 1.6" />
        </svg>
      )}
    </button>
  )
}

/** Enquanto a trava do canário está ligada, ela fica à vista. Ninguém deve
 *  descobrir que estava em modo protegido só quando uma escrita falha. */
function SeloDaTrava() {
  return (
    <span
      title="Trava do canário ligada: escrita só no projeto canário. A API aponta para produção."
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, height: 22, padding: "0 8px",
        borderRadius: 999, background: "rgba(242,121,47,.22)", color: "#FFD9BE",
        fontSize: 10.5, fontWeight: 600, letterSpacing: ".02em",
      }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true"
           style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
        <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8.5 11V8a3.5 3.5 0 1 1 7 0v3" />
      </svg>
      canário
    </span>
  )
}

function Avatar({ nome, aoSair }: { nome: string; aoSair: () => void }) {
  const iniciais = nome.trim().split(/\s+/).slice(0, 2).map((p) => p[0] ?? "").join("").toUpperCase() || "··"
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label="Conta"
        style={{
          width: 28, height: 28, marginLeft: 2, borderRadius: "50%",
          background: "rgba(255,255,255,.16)", color: "#FFFFFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10.5, fontWeight: 700,
        }}
      >
        {iniciais}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          sideOffset={6} align="end"
          style={{
            minWidth: 170, padding: 6, borderRadius: 10, background: "var(--surface)",
            border: "1px solid var(--line2)", boxShadow: "var(--sombra)", color: "var(--txt)", zIndex: 50,
          }}
        >
          <Menu.Label style={{ padding: "6px 9px", fontSize: 12, color: "var(--txt2)" }}>{nome || "—"}</Menu.Label>
          <Menu.Separator style={{ height: 1, background: "var(--line)", margin: "4px 0" }} />
          <Menu.Item
            onSelect={aoSair}
            style={{ padding: "7px 9px", borderRadius: 6, fontSize: 12.5, cursor: "pointer", outline: "none" }}
          >
            Sair
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}
